<template>
	<div v-for="(item, i) in filterItems()" :key="`item-${i}`" :class="item.outerClass">
		<ToolbarItems
			v-if="item.type === 'group'"
			:items="item.items"
			v-bind="item.props"
			v-on="item.actions ?? {}"
		/>

		<component :is="item.component" v-bind="item.props" v-on="item?.actions ?? {}">
			<template v-for="slot in item?.slots ?? []" #[slot.name]="data" :key="slot.name">
				<ToolbarItems :items="getSlotItems(slot, data)"/>
			</template>
			<template v-if="item.text" #default>
				{{ item.text }}
			</template>
		</component>

		<div v-if="item.type === 'title'" class="d-flex">
			<v-toolbar-title :class="item.class">
				{{ item.value }}
				<label v-if="item.label">
					{{ item.label }}
				</label>
			</v-toolbar-title>
		</div>

		<v-tooltip
			v-if="item.type === 'button'"
			location="bottom" :disabled="!item.hover || !item.hover.visible" :open-delay="item.openDelay ?? 0"
			:text="item?.hover?.text ?? ''">
			<template #activator="{ props }">
				<v-btn
					v-bind="props"
					:class="item.class"
					:icon="!item.displayName"
					:disabled="item.disabled"
					height="40"
					elevation="1"
					:ripple="false"
					:style="item.customStyle"
					@click.prevent="item.action(item.value)">
					<template #default>
						<v-icon v-if="!item.isCustomIcon" size="24">{{ `mdi-${item.icon}` }}</v-icon>
						<Image v-else width="20" height="20" :img="`${item.icon}.svg`"/>
						<div v-if="item.displayName" class="font-weight-bold pl-2">
							{{ item.displayName }}
						</div>
					</template>
				</v-btn>
			</template>
		</v-tooltip>

		<v-menu v-if="item.type === 'menu'">
			<template #activator="{ props }">
				<v-btn v-bind="props" :icon="item.icon" size="36"/>
			</template>
			<v-list>
				<v-list-item
					v-for="(action, index) in item.actions" :key="index" link
					:disabled="action.disabled">
					<v-list-item-title class="text-body-2" @click="handleMenuOptionClick(item.actions, index)">
						{{ action.title }}
					</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>

		<v-select
			v-if="item.type === 'select'"
			v-model="item.model"
			:items="item.options"
			:disabled="item.disabled"
			variant="outlined"
			density="compact"
			hide-details
			:style="item.customStyle"
			@update:model-value="handleSelectionChange($event, item)">
		</v-select>

		<v-menu v-if="item.type === 'datepicker'" v-model="item.open" :close-on-content-click="false">
			<template #activator="{ props }">
				<v-text-field
					v-bind="props"
					:model-value="item.model.format('YYYY-MM-DD')"
					variant="outlined"
					density="compact"
					flat
					single-line
					hide-details
					placeholder="Select date"
					append-inner-icon="mdi-calendar"
					:style="item.customStyle"
					:disabled="item.disabled"
				/>
			</template>
			<v-date-picker
				:model-value="item.model"
				color="secondary"
				hide-header
				show-adjacent-months
				@update:model-value="(date) => {item.open = false; handleSelectionChange(date, item);}">
			</v-date-picker>
		</v-menu>
	</div>
</template>

<script>
/* Libraries */
import * as _ from "lodash";

/* Modules */
import Image from "@/components/Image.vue";

/* Mixins */
import DurationFormatMixin from "@/mixins/DurationFormatMixin";

export default {
	name: "Toolbar",

	components: {Image},

	mixins: [DurationFormatMixin],

	props: ["items"],

	methods: {
		filterItems() {
			return this.items.filter(i => !_.isEmpty(i));
		},

		getSlotItems(slot, data) {
			if (_.isFunction(slot.items)) {
				return slot.items(data);
			}
			return slot.items;
		},

		handleMenuOptionClick(actions, index) {
			actions[index].click();
		},

		handleSelectionChange(value, item) {
			item.onChange(value);
		}
	}
}
</script>

<style lang="scss" scoped>
@keyframes auto-pending {
	0% {
		background-color: $ihm-green;
	}
	50% {
		background-color: $ihm-grey2;
	}
}

@keyframes manual-pending {
	0% {
		background-color: $ihm-red2;
	}
	50% {
		background-color: $ihm-grey2;
	}
}

@keyframes live_assist-pending {
	0% {
		background-color: $ihm-blue;
	}
	50% {
		background-color: $ihm-grey2;
	}
}

@keyframes split-pending {
	0% {
		background-color: $ihm-magenta;
	}
	50% {
		background-color: $ihm-grey2;
	}
}


// todo: move it under OnAir Component
.mode-btn-group {
	border-radius: 8px;

	.mode-btn {
		background-color: $ihm-grey2;
		font-size: 16px !important;
		font-weight: 600 !important;
		opacity: 1 !important;
		padding: 8px 16px !important;

		&.v-theme--dark {
			background-color: $grey-500 !important;
			border: none !important;

			&:nth-child(2) {
				border-left: 1px solid $grey-600 !important;
				border-right: 1px solid $grey-600 !important;
			}
		}

		&.v-btn--active {
			color: $ihm-white !important;

			&.auto {
				background-color: $ihm-green;

				&.pending {
					animation: 1s auto-pending infinite !important;
					color: $ihm-black !important;
				}
			}

			&.manual {
				background-color: $ihm-red2;

				&.pending {
					animation: 1s manual-pending infinite !important;
					color: $ihm-black !important;
				}
			}

			&.live_assist {
				background-color: $ihm-blue;

				&.pending {
					animation: 1s live_assist-pending infinite !important;
					color: $ihm-black !important;
				}
			}

			&.split {
				background-color: $ihm-magenta;

				&.pending {
					animation: 1s split-pending infinite !important;
					color: $ihm-black !important;
				}
			}

			&.v-theme--dark {
				color: $grey-500 !important;

				&.auto {
					background-color: $ihm-green--dark !important;
				}

				&.manual {
					background-color: $ihm-red--dark !important;
				}

				&.live_assist {
					background-color: $ihm-blue--dark !important;
				}

				&.split {
					background-color: $ihm-magenta--dark !important;
				}
			}
		}
	}

	//@deprecated: unused class after last changed
	&.micro {
		border-radius: 6px;
		height: 24px;

		.mode-btn {
			font-size: 12px !important;
			padding: 0 12px !important;
		}
	}
}
</style>
